import React, { useCallback } from 'react';
import ModalComponent from 'components/modal/modalComponent';

import { ModalButtonProps } from 'components/modal/modal.interfaces';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import { componentIds, genericActionsIds } from 'utilities/constants';
import { useGetComponent } from 'hooks/useGetComponent';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomToast } from 'layout/useToast';
import {
  Hasura_Legal_Document_Types_Enum,
  useAcceptLegalDocumentMutation,
  useAcceptUserAgreementMutation,
  UserAgreementTypes,
} from 'graphql/generated/hasura';
import { sanitize } from 'utilities/sanitizer';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormCheckboxGroup } from 'components/stepper/field-components';
import StorageService from 'utilities/storageService';
import {
  HASURA_USER_STORAGE_KEY,
  MAIN_ACCOUNT_INFO_KEY,
} from '../../../firebase/constants';
import moment from 'moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import { twJoin, twMerge } from 'tailwind-merge';
import ButtonComponent from 'components/button/buttonComponent';
import { LEGAL_DOCUMENTS_ROUTE } from '../../../utilities/routes';

const useCoppaSchemas = () => {
  const schemas = yup.object({
    child: yup
      .boolean()
      .oneOf([true], 'You need to agree')
      .required('You need to agree'),
    parent: yup
      .boolean()
      .oneOf([true], 'You need to agree')
      .required('You need to agree'),
  });
  return { schemas };
};

const childStorage = new StorageService(HASURA_USER_STORAGE_KEY);
const mainAccountStorage = new StorageService(MAIN_ACCOUNT_INFO_KEY);

interface COPPAAgreementModalProps {
  isEditState?: boolean;
}

const COPPAAgreementModal: React.FC<COPPAAgreementModalProps> = ({
  isEditState = true,
}) => {
  const navigate = useNavigate();
  const { addToast } = useCustomToast();

  const child = childStorage.getData();
  const parent = mainAccountStorage.getData();

  const { loading, data: locale } = useGetComponent({
    componentId: componentIds.COPPA_AGREEMENT,
    locale: 'en',
  });

  const [acceptUserAgreement, { loading: loadingAgreement }] =
    useAcceptUserAgreementMutation({
      refetchQueries: ['GetAgreementsByUserId'],
    });

  const [acceptLegalDocument, { loading: loadingAccept }] =
    useAcceptLegalDocumentMutation({
      variables: {
        legal_document_type: Hasura_Legal_Document_Types_Enum.CoppaAgreement,
      },
      refetchQueries: ['GetMyAcceptedLegalDocuments'],
    });

  const { data: genericAction, loading: genericActionLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [
        genericActionsIds.CANCEL,
        genericActionsIds.CONFIRM,
        genericActionsIds.CLOSE,
      ],
    });

  const handleClose = useCallback(() => navigate(-1), [navigate]);

  const { schemas } = useCoppaSchemas();
  const handleConfirm = useCallback(
    () =>
      Promise.all([
        acceptLegalDocument(),
        acceptUserAgreement({
          variables: { agreementType: UserAgreementTypes.CoppaChild },
        }),
      ])
        .then(handleClose)
        .then(() =>
          addToast({
            type: 'success',
            message:
              locale?.toastSuccess ?? 'COPPA agreement signed successfully',
          }),
        ),
    [
      acceptLegalDocument,
      acceptUserAgreement,
      addToast,
      handleClose,
      locale?.toastSuccess,
    ],
  );

  const methods = useForm({
    resolver: yupResolver(schemas),
    mode: 'onChange',
    defaultValues: {
      child: false,
      parent: false,
    },
  });

  const Buttons: ModalButtonProps[] = isEditState
    ? [
        {
          label: genericAction?.[genericActionsIds.CONFIRM].confirm,
          disabled: loadingAgreement || loadingAccept,
          showSpinner: loadingAgreement || loadingAccept,
          testID: 'accepted',
          htmlButtonType: 'submit',
          className:
            '!text-sm !font-bold !rounded !py-2.5 !leading-[20px] w-full desktop:w-auto',
        },
        {
          label: genericAction?.[genericActionsIds.CANCEL]?.cancel,
          onClick: handleClose,
          type: 'underline',
          htmlButtonType: 'button',
          className: '!text-sm !font-bold',
          testID: 'close-modal',
        },
      ]
    : [
        {
          label: genericAction?.[genericActionsIds.CLOSE]?.close,
          onClick: handleClose,
          htmlButtonType: 'button',
          className: '!text-sm !font-bold',
          testID: 'close-modal',
        },
      ];

  if (!locale || genericActionLoading || loading) {
    return null;
  }

  return (
    <ModalComponent
      title={locale?.title}
      isOpen={true}
      modalDesktopWidth="desktop:w-[80vw]"
      customContentContainerClass="relative"
      contentContainerDefaultBorder="rounded-b-10 desktop:!px-10 !px-7"
      contentPaddingY="pb-0 pt-10"
      onClose={handleClose}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={methods.handleSubmit(handleConfirm)}>
          <div className="text-med-gray-3 h-[530px] overflow-auto font-medium flex -mr-10 pr-10 flex-col gap-30">
            <div
              dangerouslySetInnerHTML={{ __html: sanitize(locale.contentWeb) }}
            />
            {isEditState && (
              <>
                <div className="w-full flex flex-col bg-white px-5 py-4 border border-gray-opacity-15 rounded-10 gap-5">
                  <FormCheckboxGroup
                    name="child"
                    className="text-sm"
                    label={
                      <>
                        <span>{locale?.child?.agree}</span>
                        <Link
                          target="_blank"
                          to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.PrivacyPolicy}`}
                          className="ml-1 text-clc-blue underline"
                        >
                          {locale?.child?.policy}
                        </Link>
                        <span className={twJoin('text-alert-negative hidden')}>
                          *
                        </span>
                      </>
                    }
                  />

                  <div className="w-full flex desktop:flex-row flex-col">
                    <div className="flex flex-col gap-1.5 desktop:w-[270px]">
                      <p className="text-neutral-800 font-semibold">
                        {locale?.child?.nameLabel}
                      </p>
                      <p className="text-med-gray-3 text-sm font-medium">
                        {child?.SENSITIVE_firstname} {child?.SENSITIVE_lastname}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5 desktop:w-[270px]">
                      <p className="text-neutral-800 font-semibold">
                        {locale?.child?.dobLabel}
                      </p>
                      <p className="text-med-gray-3 text-sm font-medium">
                        {child?.SENSITIVE_dob ?? '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="w-full flex flex-col bg-white px-5 py-4 border border-gray-opacity-15 mt-2.5 rounded-10 gap-5">
                  <FormCheckboxGroup
                    name="parent"
                    className="text-sm"
                    label={
                      <>
                        <span>{locale?.patent?.agree}</span>
                        <span className={twJoin('text-alert-negative hidden')}>
                          *
                        </span>
                      </>
                    }
                  />

                  <div className="w-full flex desktop:flex-row flex-col">
                    <div className="flex flex-col gap-1.5 desktop:w-[270px]">
                      <p className="text-neutral-800 font-semibold">
                        {locale?.patent?.nameLabel}
                      </p>
                      <p className="text-med-gray-3 text-sm font-medium">
                        {parent?.name}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1.5 desktop:w-[270px]">
                      <p className="text-neutral-800 font-semibold">
                        {locale?.patent?.signLabel}
                      </p>
                      <p className="text-med-gray-3 text-sm font-medium">
                        {moment().format(STANDARD_DATE)}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div
            className={twMerge(
              'sticky rounded-b-10 z-10 bottom-0 flex flex-col mt-30 desktop:px-10 desktop:py-7 desktop:-mx-10 px-7 -mx-7 py-5 desktop:w-[calc(100%_+_80px)] w-[calc(100%_+_56px)] gap-5 bg-white desktop:flex-row-reverse',
            )}
          >
            {Buttons.length > 0 && (
              <div
                className={twMerge(
                  'flex flex-row-reverse gap-5 desktop:gap-30',
                )}
              >
                {Buttons.map((button, index) => (
                  <ButtonComponent key={index} {...button}>
                    {button.label}
                  </ButtonComponent>
                ))}
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </ModalComponent>
  );
};

export default COPPAAgreementModal;
